import { useEffect, useState, useCallback } from 'react';

interface CacheStats {
  tileCount: number;
  estimatedSize: number;
  maxTiles: number;
}

interface UseTileCacheReturn {
  isSupported: boolean;
  isRegistered: boolean;
  cacheStats: CacheStats | null;
  clearCache: () => Promise<void>;
  refreshStats: () => void;
}

/**
 * Hook per gestire il Service Worker di caching dei tiles della mappa.
 * Registra automaticamente il SW e fornisce metodi per gestire la cache.
 */
export function useTileCache(): UseTileCacheReturn {
  const [isSupported, setIsSupported] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);

  // Registra il Service Worker
  useEffect(() => {
    // Verifica supporto Service Worker
    if (!('serviceWorker' in navigator)) {
      console.warn('[TileCache] Service Workers non supportati in questo browser');
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    const registerServiceWorker = async () => {
      try {
        // Registra il SW per il caching dei tiles
        const registration = await navigator.serviceWorker.register('/tile-cache-sw.js', {
          scope: '/',
        });

        console.log('[TileCache] Service Worker registrato con successo:', registration.scope);
        setIsRegistered(true);

        // Ascolta aggiornamenti del SW
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'activated') {
                console.log('[TileCache] Nuovo Service Worker attivato');
              }
            });
          }
        });
      } catch (error) {
        console.error('[TileCache] Errore nella registrazione del Service Worker:', error);
        setIsRegistered(false);
      }
    };

    registerServiceWorker();

    // Cleanup non necessario - il SW rimane registrato
  }, []);

  // Ascolta messaggi dal Service Worker
  useEffect(() => {
    if (!isSupported) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'CACHE_STATS') {
        setCacheStats(event.data.data);
      }
      if (event.data?.type === 'TILE_CACHE_CLEARED') {
        setCacheStats(null);
        console.log('[TileCache] Cache tiles svuotata');
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [isSupported]);

  // Richiedi statistiche cache iniziali quando il SW è pronto
  useEffect(() => {
    if (!isRegistered) return;

    // Aspetta che il SW sia pronto
    const getStats = async () => {
      const registration = await navigator.serviceWorker.ready;
      registration.active?.postMessage({ type: 'GET_CACHE_STATS' });
    };

    // Piccolo delay per assicurarsi che il SW sia completamente attivo
    const timeout = setTimeout(getStats, 500);

    return () => clearTimeout(timeout);
  }, [isRegistered]);

  // Svuota la cache dei tiles
  const clearCache = useCallback(async () => {
    if (!isSupported) {
      console.warn('[TileCache] Service Workers non supportati');
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    registration.active?.postMessage({ type: 'CLEAR_TILE_CACHE' });
  }, [isSupported]);

  // Aggiorna le statistiche della cache
  const refreshStats = useCallback(async () => {
    if (!isSupported) return;

    const registration = await navigator.serviceWorker.ready;
    registration.active?.postMessage({ type: 'GET_CACHE_STATS' });
  }, [isSupported]);

  return {
    isSupported,
    isRegistered,
    cacheStats,
    clearCache,
    refreshStats,
  };
}

export default useTileCache;

